import { type CSSProperties, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AttributionBadge from "../components/Attribution";
import Icon from "../components/Icon";
import {
  api,
  type HistoryItem,
  type LibrarySource,
  type Project,
  type Thread,
} from "../api/client";
import { setCurrentProject } from "../hooks/useCurrentProject";
import {
  FIELD_NOTE,
  OPEN_QUESTIONS,
  STUDY_DELIVERABLES,
  TODAY_ACTIVITY,
  profileForStudy,
  type StudyDeliverable,
} from "../data/studyFixtures";

const STUDY_COLORS = ["#5e8d6f", "#a87c4a", "#3a6688", "#5b3a8c", "#6b6557", "#b04a3a"];
const ARCHIVE_PROJECT_FIELDS = { archived: true } as const;
const RESTORE_PROJECT_FIELDS = { archived: false } as const;

function notifyProjectsChanged() {
  window.dispatchEvent(new Event("abby:projects-changed"));
}

function daysSince(ms: number): number {
  if (!ms) return 0;
  return Math.max(1, Math.ceil((Date.now() - ms) / 86_400_000));
}

function isSameCalendarDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDate(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  const now = new Date();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

function formatWhen(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  const now = new Date();
  if (isSameCalendarDate(d, now)) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return formatDate(ms);
}

function formatUpdatedLabel(ms: number): string {
  if (!ms) return "updated";
  return `updated ${formatDate(ms)}`;
}

function itemTime(it: HistoryItem): number {
  return it.kind === "chat" ? it.updated_at : it.created_at;
}

function itemLabel(it: HistoryItem): string {
  if (it.kind === "chat") return "chat";
  if (it.kind === "task") return "task";
  return "research";
}

export default function StudiesPage() {
  const { studyId } = useParams();
  const numericStudyId = studyId ? Number(studyId) : null;
  const [projects, setProjects] = useState<Project[]>([]);
  const [inboxThreads, setInboxThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newStudyName, setNewStudyName] = useState("");
  const navigate = useNavigate();

  const activeStudy = useMemo(
    () => (numericStudyId == null ? null : projects.find((p) => p.id === numericStudyId) ?? null),
    [numericStudyId, projects],
  );

  function refreshProjects() {
    setLoading(true);
    setError(null);
    Promise.all([
      api.listProjects(true),
      api.listThreads({ inbox: true }),
    ])
      .then(([ps, threads]) => {
        setProjects(ps);
        setInboxThreads(threads);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refreshProjects();
  }, []);

  async function createStudy() {
    const name = newStudyName.trim();
    if (!name) return;
    const color = STUDY_COLORS[projects.length % STUDY_COLORS.length];
    try {
      const p = await api.createProject(name, color);
      setProjects((ps) => [...ps, p]);
      notifyProjectsChanged();
      setNewStudyName("");
      setCreating(false);
      setCurrentProject({ kind: "project", id: p.id });
      navigate(`/studies/${p.id}`);
    } catch (e) {
      setError(String(e));
    }
  }

  async function setStudyArchived(project: Project, archived: boolean) {
    if (archived) {
      const ok = window.confirm(
        `Archive study "${project.name}"?\n\nIt will be hidden from active studies. Chats, tasks, docs, and sources stay intact and can be restored later.`,
      );
      if (!ok) return;
    }
    try {
      const fields = archived ? ARCHIVE_PROJECT_FIELDS : RESTORE_PROJECT_FIELDS;
      const updated = await api.updateProject(project.id, fields);
      setProjects((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
      notifyProjectsChanged();
      if (archived && numericStudyId === project.id) {
        setCurrentProject({ kind: "all" });
        navigate("/studies");
      }
    } catch (e) {
      setError(String(e));
    }
  }

  if (numericStudyId != null) {
    return (
      <StudyWorkspace
        study={activeStudy}
        loadingProjects={loading}
        onBack={() => navigate("/studies")}
        onSetArchived={setStudyArchived}
      />
    );
  }

  const activeStudies = projects.filter((p) => !p.archived);
  const archivedStudies = projects.filter((p) => p.archived);

  return (
    <>
      <div className="main-header studies-header">
        <div>
          <span className="crumb">/ home</span>
          <h2>Studies</h2>
        </div>
        <span className="spacer" />
        <button
          type="button"
          className="ghost"
          onClick={() => {
            setCurrentProject({ kind: "inbox" });
            navigate("/inbox");
          }}
        >
          Inbox · {inboxThreads.length}
        </button>
        <button type="button" className="primary icon-button" onClick={() => setCreating((v) => !v)}>
          <Icon name="plus" size={15} />
          New study
        </button>
      </div>

      <div className="main-body studies-body">
        {error && <p className="error">{error}</p>}

        {creating && (
          <div className="study-create-panel">
            <label>
              <span>Study name</span>
              <input
                autoFocus
                value={newStudyName}
                onChange={(e) => setNewStudyName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") createStudy();
                  if (e.key === "Escape") {
                    setCreating(false);
                    setNewStudyName("");
                  }
                }}
                placeholder="New study name"
              />
            </label>
            <button type="button" className="primary" onClick={createStudy} disabled={!newStudyName.trim()}>
              Create
            </button>
          </div>
        )}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : activeStudies.length === 0 ? (
          <div className="empty-study-state">
            <h3>No studies yet</h3>
            <p className="muted">Create the first one to group conversations, tasks, research runs, and saved docs.</p>
            <button type="button" className="primary icon-button" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              New study
            </button>
          </div>
        ) : (
          <div className="studies-grid">
            {activeStudies.map((p) => (
              <article
                key={p.id}
                className="study-card"
                style={{ "--study-color": p.color || STUDY_COLORS[0] } as CSSProperties}
              >
                <span className="study-card-meta">
                  <span className="project-dot" style={{ background: p.color || "var(--accent)" }} />
                  <span>{profileForStudy(p).site}</span>
                  <span>·</span>
                  <span>{profileForStudy(p).phase}</span>
                </span>
                <strong>{p.name}</strong>
                <AttributionBadge createdBy={p.created_by} compact />
                <span className="study-card-question">{profileForStudy(p).question}</span>
                <span className="study-card-stats">
                  <span><b>{STUDY_DELIVERABLES.length}</b> delivs</span>
                  <span><b>{p.counts.total}</b> items</span>
                  <span><b>{daysSince(p.created_at)}d</b> in flight</span>
                  <span><b>{p.counts.chat}</b> threads</span>
                </span>
                <span className="study-card-actions">
                  <button
                    type="button"
                    className="ghost-link"
                    onClick={() => {
                      setCurrentProject({ kind: "project", id: p.id });
                      navigate(`/studies/${p.id}`);
                    }}
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    className="ghost-link study-archive-button"
                    onClick={() => setStudyArchived(p, true)}
                  >
                    Archive study
                  </button>
                </span>
              </article>
            ))}
            <button type="button" className="study-card study-card-new" onClick={() => setCreating(true)}>
              <span className="study-new-plus">+</span>
              <strong>New study</strong>
              <span className="study-card-question">
                Create a scoped workspace for deliverables, sources, chats, tasks, and notes.
              </span>
            </button>
          </div>
        )}

        {archivedStudies.length > 0 && (
          <section className="archived-studies">
            <h3>Archived</h3>
            <div className="studies-grid compact">
              {archivedStudies.map((p) => (
                <article
                  key={p.id}
                  className="study-card archived"
                  style={{ "--study-color": p.color || STUDY_COLORS[0] } as CSSProperties}
                >
                  <span className="study-card-meta">
                    <span className="project-dot" style={{ background: p.color || "var(--accent)" }} />
                    archived
                  </span>
                  <strong>{p.name}</strong>
                  <span className="study-card-actions">
                    <button type="button" className="ghost-link" onClick={() => navigate(`/studies/${p.id}`)}>
                      Open
                    </button>
                    <button
                      type="button"
                      className="ghost-link study-restore-button"
                      onClick={() => setStudyArchived(p, false)}
                    >
                      Restore study
                    </button>
                  </span>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function StudyWorkspace({
  study,
  loadingProjects,
  onBack,
  onSetArchived,
}: {
  study: Project | null;
  loadingProjects: boolean;
  onBack: () => void;
  onSetArchived: (project: Project, archived: boolean) => Promise<void>;
}) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [library, setLibrary] = useState<LibrarySource[]>([]);
  const [loading, setLoading] = useState(false);
  const [composer, setComposer] = useState("");
  const [mobileTab, setMobileTab] = useState<"today" | "deliverables" | "activity" | "notes">("today");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!study) return;
    setCurrentProject({ kind: "project", id: study.id });
    setLoading(true);
    setError(null);
    Promise.all([
      api.listHistory({ project_id: study.id }),
      api.listThreads({ project_id: study.id }),
      api.listLibrary(),
    ])
      .then(([items, studyThreads, sources]) => {
        setHistory(items);
        setThreads(studyThreads);
        setLibrary(sources);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [study?.id]);

  if (loadingProjects) {
    return (
      <div className="main-body">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (!study) {
    return (
      <>
        <div className="main-header">
          <button type="button" className="ghost" onClick={onBack}>
            Back to studies
          </button>
        </div>
        <div className="main-body">
          <p className="error">Study not found.</p>
        </div>
      </>
    );
  }

  const currentStudyId = study.id;
  const profile = profileForStudy(study);
  const latest = [...history].sort((a, b) => itemTime(b) - itemTime(a))[0] ?? null;
  const total = Math.max(study.counts.total, 1);
  const firstDeliverable = STUDY_DELIVERABLES[0] ?? null;
  const objectCards = [
    ...(firstDeliverable
      ? [{ label: "Deliverables", value: STUDY_DELIVERABLES.length, icon: "doc" as const, to: `/studies/${study.id}/deliverables/${firstDeliverable.key}` }]
      : []),
    { label: "Conversations", value: study.counts.chat, icon: "chat" as const, to: "/chat" },
    { label: "Task runs", value: study.counts.task, icon: "tasks" as const, to: "/tasks" },
    { label: "Research runs", value: study.counts.research, icon: "research" as const, to: "/research" },
  ];

  function navigateWithScope(to: string) {
    setCurrentProject({ kind: "project", id: currentStudyId });
    navigate(to);
  }

  function startScopedChat() {
    setCurrentProject({ kind: "project", id: currentStudyId });
    if (composer.trim()) {
      try {
        localStorage.setItem("abby_draft:chat:new", JSON.stringify(composer.trim()));
      } catch {
        /* storage unavailable */
      }
    }
    navigate("/chat");
  }

  if (study.counts.total === 0 && !loading) {
    return (
      <EmptyStudyWorkspace
        study={study}
        profile={profile}
        composer={composer}
        onComposerChange={setComposer}
        onBack={onBack}
        onStartChat={startScopedChat}
        onNavigate={navigateWithScope}
      />
    );
  }

  return (
    <>
      <div
        className="study-spine"
        style={{ background: study.color || "var(--accent)" }}
        aria-hidden="true"
      />
      <div className="main-header studies-header workspace-header">
        <button type="button" className="ghost" onClick={onBack}>
          Studies
        </button>
        <div>
          <span className="crumb">/ study workspace</span>
          <h2>{study.name}</h2>
          <AttributionBadge createdBy={study.created_by} compact />
        </div>
        <span className="spacer" />
        {study && (
          <button
            type="button"
            className={`ghost ${study.archived ? "study-restore-button" : "workspace-danger"}`}
            onClick={() => onSetArchived(study, !study.archived)}
          >
            {study.archived ? "Restore study" : "Archive study"}
          </button>
        )}
        <button type="button" className="primary icon-button" onClick={startScopedChat}>
          <Icon name="chat" size={15} />
          New chat
        </button>
      </div>

      <div className="main-body study-workspace">
        {error && <p className="error">{error}</p>}
        {loading && <p className="muted">Loading workspace…</p>}

        <section className="workspace-hero" style={{ "--study-color": study.color || "var(--accent)" } as CSSProperties}>
          <div>
            <span className="study-status">
              <span className="project-dot" style={{ background: study.color || "var(--accent)" }} />
              {profile.phase}
            </span>
            <AttributionBadge createdBy={study.created_by} compact />
            <h1>{study.name}</h1>
            <p>{profile.question}</p>
          </div>
          <div className="workspace-stat-block">
            <span><b>{daysSince(study.created_at)}</b> days</span>
            <span><b>{STUDY_DELIVERABLES.length}</b> delivs</span>
            <span><b>{study.counts.total}</b> items</span>
            <span><b>{formatWhen(study.updated_at)}</b> {formatUpdatedLabel(study.updated_at)}</span>
          </div>
        </section>

        <section className="since-banner">
          <span className="label">Since you were last here</span>
          {latest ? (
            <span>
              {itemLabel(latest)} · {latest.title} · {formatWhen(itemTime(latest))}
            </span>
          ) : (
            <span>No activity in this study yet.</span>
          )}
        </section>

        <TodayStrip />

        <SectionHeading title="Deliverables" meta={`${STUDY_DELIVERABLES.length} active`} />
        <section className="deliverables-grid">
          {STUDY_DELIVERABLES.length === 0 ? (
            <div className="empty-inline">No deliverables saved yet.</div>
          ) : (
            STUDY_DELIVERABLES.map((d) => (
              <DeliverableCard
                key={d.key}
                deliverable={d}
                onOpen={() => navigateWithScope(`/studies/${study.id}/deliverables/${d.key}`)}
              />
            ))
          )}
        </section>

        <section className="workspace-objects">
          {objectCards.map((card) => (
            <button
              key={card.label}
              type="button"
              className="workspace-object-card"
              onClick={() => navigateWithScope(card.to)}
            >
              <span className="object-icon"><Icon name={card.icon} size={17} /></span>
              <span className="object-label">{card.label}</span>
              <strong>{card.value}</strong>
              <span className="object-meter">
                <span style={{ width: `${Math.max(8, (card.value / total) * 100)}%` }} />
              </span>
            </button>
          ))}
        </section>

        <div className="workspace-grid">
          <section className="workspace-panel">
            <div className="panel-heading">
              <h3>Earlier activity</h3>
              <button type="button" className="ghost-link" onClick={() => navigate("/history")}>
                View all
              </button>
            </div>
            <div className="activity-feed">
              {history.length === 0 ? (
                <p className="muted">No activity yet.</p>
              ) : (
                history.slice(0, 8).map((it) => (
                  <button
                    key={`${it.kind}-${it.id}`}
                    type="button"
                    className="activity-row"
                    onClick={() => {
                      if (it.kind === "chat") navigate(`/chat/${it.id}`);
                      else if (it.kind === "task") navigate(`/tasks/${it.template_key}`);
                      else navigateWithScope("/research");
                    }}
                  >
                    <span className={`kind-badge ${it.kind}`}>{itemLabel(it)}</span>
                    <span className="activity-main">
                      <strong>{it.title}</strong>
                      <AttributionBadge createdBy={it.created_by} compact />
                      <span>{it.preview}</span>
                    </span>
                    <span className="activity-time">{formatWhen(itemTime(it))}</span>
                  </button>
                ))
              )}
            </div>
          </section>

          <aside className="workspace-rail">
            <section className="workspace-panel">
              <div className="panel-heading">
                <h3>Open threads</h3>
                <button type="button" className="ghost-link" onClick={startScopedChat}>
                  New
                </button>
              </div>
              <div className="thread-list">
                {threads.length === 0 ? (
                  <p className="muted">No conversations here.</p>
                ) : (
                  threads.slice(0, 5).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="thread-card"
                      onClick={() => navigate(`/chat/${t.id}`)}
                    >
                      <strong className="thread-title">{t.title}</strong>
                      <span className="thread-time">{formatWhen(t.updated_at)}</span>
                      <AttributionBadge createdBy={t.created_by} compact />
                    </button>
                  ))
                )}
              </div>
            </section>

            <QuestionList />

            <section className="workspace-panel">
              <div className="panel-heading">
                <h3>Sources in play</h3>
                <button type="button" className="ghost-link" onClick={() => navigateWithScope("/library")}>
                  Library
                </button>
              </div>
              <HotSources library={library} onOpenLibrary={() => navigateWithScope("/library")} />
            </section>

            <FieldNote />
          </aside>
        </div>

        <MobileWorkspace
          tab={mobileTab}
          onTabChange={setMobileTab}
          study={study}
          profile={profile}
          history={history}
          onOpenDeliverable={(d) => navigateWithScope(`/studies/${study.id}/deliverables/${d.key}`)}
        />

        <form
          className="scoped-composer"
          onSubmit={(e) => {
            e.preventDefault();
            startScopedChat();
          }}
        >
          <span className="scope-chip">
            <span className="project-dot" style={{ background: study.color || "var(--accent)" }} />
            scoped to {study.name}
          </span>
          <input
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            placeholder="Ask, draft, or sanity-check something in this study…"
          />
          <div className="composer-actions">
            <button type="button" className="ghost-link" onClick={() => navigateWithScope("/tasks")}>
              Task
            </button>
            <button type="button" className="ghost-link" onClick={() => navigateWithScope("/research")}>
              Research
            </button>
          </div>
          <button type="submit" className="primary icon-only" aria-label="Start scoped chat">
            <Icon name="send" size={16} />
          </button>
        </form>
      </div>
    </>
  );
}

function SectionHeading({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="study-section-heading">
      <h2>{title}</h2>
      <span className="rule" />
      <span className="tag">{meta}</span>
    </div>
  );
}

function DeliverableCard({
  deliverable,
  onOpen,
}: {
  deliverable: StudyDeliverable;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={`deliverable-card ${deliverable.warning ? "warning" : ""} ${deliverable.cite ? "cite" : ""}`}
      onClick={onOpen}
    >
      <div className="deliverable-kind">
        {deliverable.kind}
        <span>{deliverable.version}</span>
      </div>
      <h3>{deliverable.title}</h3>
      <p>{deliverable.status}</p>
      <Sparkline values={deliverable.spark} />
      <div className="deliverable-next">
        <span>next</span>
        <strong>{deliverable.next}</strong>
        <span>{deliverable.nextLabel}</span>
      </div>
      <div className="deliverable-foot">
        <span className={`deliverable-badge ${deliverable.badgeKind}`}>{deliverable.badge}</span>
        <span>{deliverable.touched}</span>
      </div>
    </button>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(...values, 1);
  return (
    <div className="deliverable-spark" aria-hidden="true">
      {values.map((value, i) => (
        <span key={`${value}-${i}`} style={{ height: `${Math.max(12, (value / max) * 100)}%` }} />
      ))}
    </div>
  );
}

function TodayStrip() {
  if (TODAY_ACTIVITY.length === 0) return null;

  return (
    <section className="today-strip">
      <div className="today-strip-head">
        <span>today</span>
        <span>live workspace activity</span>
      </div>
      {TODAY_ACTIVITY.map((item, index) => (
        <div key={`${item.time}-${item.title}`} className={`today-row ${index === 0 ? "now" : ""}`}>
          <span className="today-time">{item.time}</span>
          <span className="today-main">
            <strong>{item.title}</strong>
            <span>{item.sub}</span>
          </span>
          <span className={`kind-badge ${item.kind}`}>{item.meta}</span>
        </div>
      ))}
    </section>
  );
}

function QuestionList() {
  if (OPEN_QUESTIONS.length === 0) return null;

  return (
    <section className="workspace-panel">
      <div className="panel-heading">
        <h3>Open questions</h3>
        <span className="panel-count">{OPEN_QUESTIONS.filter((q) => !q.resolved).length} open</span>
      </div>
      <div className="question-list">
        {OPEN_QUESTIONS.map((q) => (
          <div key={q.text} className={`question-row ${q.resolved ? "resolved" : ""}`}>
            <strong>{q.text}</strong>
            <span>{q.source}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function HotSources({
  library,
  onOpenLibrary,
}: {
  library: LibrarySource[];
  onOpenLibrary: () => void;
}) {
  const apiSources = library.slice(0, 4);
  if (apiSources.length > 0) {
    return (
      <div className="source-list">
        {apiSources.map((s, i) => (
          <button key={s.id} type="button" className="source-card" onClick={onOpenLibrary}>
            <span className="source-number">{i + 1}</span>
            <strong className="source-title">{s.title}</strong>
            <span className="source-meta">
              {s.authors || "Unknown author"}
              {s.year ? ` · ${s.year}` : ""}
            </span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <p className="muted">No library sources used yet.</p>
  );
}

function FieldNote() {
  if (!FIELD_NOTE) return null;

  return (
    <section className="workspace-panel field-note-panel">
      <div className="panel-heading">
        <h3>Field-notes peek</h3>
        <span className="panel-count">May 19</span>
      </div>
      <div className="field-note">
        <span>{FIELD_NOTE.title}</span>
        <p>{FIELD_NOTE.body}</p>
        <p>{FIELD_NOTE.next}</p>
      </div>
    </section>
  );
}

function EmptyStudyWorkspace({
  study,
  profile,
  composer,
  onComposerChange,
  onBack,
  onStartChat,
  onNavigate,
}: {
  study: Project;
  profile: ReturnType<typeof profileForStudy>;
  composer: string;
  onComposerChange: (value: string) => void;
  onBack: () => void;
  onStartChat: () => void;
  onNavigate: (to: string) => void;
}) {
  const cards = [
    {
      n: 1,
      label: "research question",
      title: "Sharpen the question",
      body: "Paste what you have. Abby can suggest a methods stub, sampling risks, and missing evidence.",
      action: "paste a draft",
      onClick: onStartChat,
    },
    {
      n: 2,
      label: "existing docs",
      title: "Bring in what you have",
      body: "Drop a PDF, paste a URL, or add prior notes. Sources stay visually scoped to this study.",
      action: "upload or paste a URL",
      onClick: () => onNavigate("/library"),
    },
    {
      n: 3,
      label: "research agent",
      title: "Run a literature pass",
      body: "Describe the methodological, theoretical, or regional literature you need surfaced.",
      action: "describe what you need",
      onClick: () => onNavigate("/research"),
    },
    {
      n: 4,
      label: "just talk",
      title: "Start a conversation",
      body: "Say what is on your mind. The first chat usually surfaces the real deliverables.",
      action: "ask anything",
      onClick: onStartChat,
    },
  ];

  return (
    <>
      <div className="study-spine" style={{ background: study.color || "var(--accent)" }} aria-hidden="true" />
      <div className="main-header studies-header workspace-header">
        <button type="button" className="ghost" onClick={onBack}>
          Studies
        </button>
        <div>
          <span className="crumb">/ study workspace</span>
          <h2>{study.name}</h2>
          <AttributionBadge createdBy={study.created_by} compact />
        </div>
      </div>
      <div className="main-body study-workspace empty-study-workspace">
        <section className="workspace-hero" style={{ "--study-color": study.color || "var(--accent)" } as CSSProperties}>
          <div>
            <span className="study-status">
              <span className="project-dot" style={{ background: study.color || "var(--accent)" }} />
              {profile.phase}
            </span>
            <AttributionBadge createdBy={study.created_by} compact />
            <h1>{study.name}</h1>
            <p className="question-placeholder">+ add a research question to anchor the study</p>
          </div>
          <div className="workspace-stat-block">
            <span><b>0</b> days</span>
            <span><b>0</b> delivs</span>
            <span><b>0</b> items</span>
          </div>
        </section>

        <SectionHeading title="Start with one of these" meta="4 entry points" />
        <section className="empty-entry-grid">
          {cards.map((card) => (
            <button key={card.n} type="button" className="empty-entry-card" onClick={card.onClick}>
              <span className="stamp"><b>{card.n}</b>{card.label}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <span className="act">{card.action}</span>
            </button>
          ))}
        </section>

        <form
          className="scoped-composer"
          onSubmit={(e) => {
            e.preventDefault();
            onStartChat();
          }}
        >
          <span className="scope-chip">
            <span className="project-dot" style={{ background: study.color || "var(--accent)" }} />
            scoped to {study.name}
          </span>
          <input
            value={composer}
            onChange={(e) => onComposerChange(e.target.value)}
            placeholder="Ask the first question in this study..."
          />
          <button type="submit" className="primary icon-only" aria-label="Start scoped chat">
            <Icon name="send" size={16} />
          </button>
        </form>
      </div>
    </>
  );
}

function MobileWorkspace({
  tab,
  onTabChange,
  study,
  profile,
  history,
  onOpenDeliverable,
}: {
  tab: "today" | "deliverables" | "activity" | "notes";
  onTabChange: (tab: "today" | "deliverables" | "activity" | "notes") => void;
  study: Project;
  profile: ReturnType<typeof profileForStudy>;
  history: HistoryItem[];
  onOpenDeliverable: (d: StudyDeliverable) => void;
}) {
  const tabs: Array<typeof tab> = ["today", "deliverables", "activity", "notes"];
  return (
    <section className="mobile-workspace">
      <div className="mobile-study-head">
        <span className="spine" style={{ background: study.color || "var(--accent)" }} />
        <span className="crumb">
          <span className="project-dot" style={{ background: study.color || "var(--accent)" }} />
          {profile.site} - day {daysSince(study.created_at)}
        </span>
        <h2>{study.name}</h2>
        <p>{profile.question}</p>
      </div>
      <div className="mobile-tabs">
        {tabs.map((t) => (
          <button key={t} type="button" className={tab === t ? "active" : ""} onClick={() => onTabChange(t)}>
            {t}
          </button>
        ))}
      </div>
      {tab === "today" && <TodayStrip />}
      {tab === "deliverables" && (
        <div className="mobile-deliverables">
          {STUDY_DELIVERABLES.length === 0 ? (
            <p className="muted">No deliverables saved yet.</p>
          ) : (
            STUDY_DELIVERABLES.slice(0, 3).map((d) => (
              <button
                key={d.key}
                type="button"
                className={`mobile-deliverable ${d.warning ? "warning" : ""}`}
                onClick={() => onOpenDeliverable(d)}
              >
                <span>{d.kind}<em>{d.version}</em></span>
                <strong>{d.title}</strong>
                <small>{d.nextLabel} - {d.next}</small>
              </button>
            ))
          )}
        </div>
      )}
      {tab === "activity" && (
        <div className="activity-feed mobile-feed">
          {history.slice(0, 5).map((it) => (
            <div key={`${it.kind}-${it.id}`} className="activity-row">
              <span className={`kind-badge ${it.kind}`}>{itemLabel(it)}</span>
              <span className="activity-main">
                <strong>{it.title}</strong>
                <AttributionBadge createdBy={it.created_by} compact />
                <span>{it.preview}</span>
              </span>
              <span className="activity-time">{formatWhen(itemTime(it))}</span>
            </div>
          ))}
          {history.length === 0 && <p className="muted">No activity yet.</p>}
        </div>
      )}
      {tab === "notes" && <FieldNote />}
    </section>
  );
}
